import React, { useEffect, useState } from 'react';

import { matchSorter, MatchSorterOptions } from 'match-sorter';

import useTranslation from '@/utils/hooks/translation/useTranslation';

//type
import type {
    ChildrenRenderProps,
    OptionType,
    SelectComponentProps,
    SelectedOptionHelper
} from '@/components/base/Select/SelectComponent/types';

import Icon from '@/components/base/Icon';
// Components
import Input from '@/components/base/Input';
import { Option, Select } from '@/components/base/Select/SelectComponent';

type SelectFilterProps<TOption, TSelected, TMultiple, TSetSelected> = Omit<
    SelectComponentProps<TOption, TSelected, TMultiple, TSetSelected>,
    'children'
> & {
    children?: ChildrenRenderProps<TOption>;
    sortOptions?: MatchSorterOptions<TOption>;
    idInput?: string;
    onSearch?: (searchValue: string) => void;
};

export function SelectFilterComponent<
    TOption extends OptionType,
    TSelected extends SelectedOptionHelper<TOption, TMultiple>,
    TMultiple extends boolean,
    TSetSelected extends (args: TSelected) => void
>({
    options = [],
    sortOptions = { keys: [{ threshold: matchSorter.rankings.CONTAINS, key: 'label' }] },
    idInput,
    children,
    onSearch,
    ...restProps
}: SelectFilterProps<TOption, TSelected, TMultiple, TSetSelected>) {
    const { t } = useTranslation('commons');
    const [optionsData, setOptionsData] = useState(options);
    const [filter, setFilter] = useState('');

    useEffect(() => {
        if (onSearch) {
            setOptionsData(options);
            return;
        }

        if (filter.trim() !== '') {
            return setOptionsData(matchSorter(options, filter.trim(), sortOptions));
        }
        return setOptionsData(options);
    }, [filter, JSON.stringify(options), onSearch]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.code === 'Space') {
            e.stopPropagation();
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilter(e.target.value);

        if (onSearch) {
            onSearch(e.target.value);
        }
    };

    const handleInput = (e: React.ClipboardEvent<HTMLInputElement>) => {
        if (onSearch && e.currentTarget.value !== filter) {
            onSearch(e.currentTarget.value);
        }
    };

    return (
        <Select
            layoutOptions={{ hasFilter: true, ...restProps?.layoutOptions }}
            options={null}
            {...restProps}>
            <div className="sticky left-0 top-0 z-20 w-full bg-white pt-1 dark:bg-icoDarkMode-anthracite">
                <Input
                    data-testid={`select-input-${idInput}`}
                    beforeInput={<Icon icon="search" />}
                    inputStyle="search_select"
                    id={idInput}
                    className="mb-1"
                    noBorder={true}
                    value={filter}
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                    onInput={handleInput}
                />
            </div>
            {!optionsData?.length ? (
                <div className="flex justify-center p-1">{t('select_no_options')}</div>
            ) : (
                <Option
                    options={optionsData}
                    isMultiple={restProps.layoutOptions?.isMultiple}
                    selected={restProps.selected}
                    size={restProps.panelOptions?.size}
                    isTruncate={restProps.panelOptions?.isTruncate}
                    forcedDisplay={restProps.layoutOptions?.forcedDisplay}>
                    {children}
                </Option>
            )}
        </Select>
    );
}
